
import { useState, useEffect, useMemo } from 'react';

export interface PasswordRules {
  minLength: boolean;
  hasLower: boolean;
  hasUpper: boolean;
  hasNumber: boolean;
  hasSpecial: boolean;
  passwordsMatch: boolean;
}

export type PasswordStrength = 'weak' | 'medium' | 'strong' | null;

export interface UsePasswordValidationOptions {
  enableConfirmPassword?: boolean;
}

export function usePasswordValidation(options: UsePasswordValidationOptions = {}) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);

  const { enableConfirmPassword = false } = options;

  const passwordRules = useMemo((): PasswordRules => {
    if (!password) {
      return {
        minLength: false,
        hasLower: false,
        hasUpper: false,
        hasNumber: false,
        hasSpecial: false,
        passwordsMatch: enableConfirmPassword ? confirmPassword === password && confirmPassword !== '' : true
      };
    }

    return {
      minLength: password.length >= 8,
      hasLower: /[a-z]/.test(password),
      hasUpper: /[A-Z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      passwordsMatch: enableConfirmPassword ? confirmPassword === password && confirmPassword !== '' : true
    };
  }, [password, confirmPassword, enableConfirmPassword]);

  const passwordStrength = useMemo((): PasswordStrength => {
    if (!password) return null;

    const score = Object.values(passwordRules).filter(Boolean).length - (passwordRules.passwordsMatch ? 1 : 0);
    
    if (score <= 2) return 'weak';
    if (score <= 4) return 'medium';
    return 'strong';
  }, [password, passwordRules]);

  // Check if passwords match when confirm password changes
  useEffect(() => {
    // This effect ensures passwordRules is recalculated when confirmPassword changes
  }, [confirmPassword, password]);

  const shouldShowRules = password && passwordTouched && !passwordFocused;

  const isValid = useMemo(() => {
    if (!password || password.length < 8) return false;
    
    const rulesMet = Object.values(passwordRules).filter(Boolean).length - (enableConfirmPassword ? 1 : 0);
    const minRulesRequired = enableConfirmPassword ? 4 : 3; // Include passwordsMatch in count for confirm password scenarios
    
    return rulesMet >= minRulesRequired && (!enableConfirmPassword || passwordRules.passwordsMatch);
  }, [passwordRules, password, enableConfirmPassword]);

  return {
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    passwordRules,
    passwordStrength,
    passwordFocused,
    setPasswordFocused,
    passwordTouched,
    setPasswordTouched,
    shouldShowRules,
    isValid,
    enableConfirmPassword
  };
}
